import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Server,
  Database,
  Wifi,
  HardDrive,
  Cpu,
  Users,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import CosmicCard from '../ui/CosmicCard';
import CosmicButton from '../ui/CosmicButton';
import LoadingSpinner from '../ui/LoadingSpinner';
import { TenantProvisioningService } from '@/services/tenant-provisioning';

interface TenantHealth {
  id: string;
  name: string;
  subdomain: string;
  status: 'healthy' | 'warning' | 'critical' | 'offline';
  uptime: number;
  responseTime: number;
  errorRate: number;
  resourceUsage: {
    cpu: number;
    memory: number;
    storage: number;
    connections: number;
  };
  lastChecked: string;
  issues?: string[];
}

const statusColors = {
  healthy: 'text-green-500',
  warning: 'text-yellow-500',
  critical: 'text-red-500',
  offline: 'text-gray-500'
};

const statusIcons = {
  healthy: <CheckCircle className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  critical: <AlertTriangle className="w-5 h-5" />,
  offline: <Clock className="w-5 h-5" />
};

export const TenantHealthDashboard: React.FC = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);

  // Fetch tenant health data
  const { data: healthData, isLoading, refetch } = useQuery<TenantHealth[]>({
    queryKey: ['tenant-health', selectedTimeRange],
    queryFn: async (): Promise<TenantHealth[]> => {
      // Mock data for now - replace with actual API call
      return [
        {
          id: 'tenant-1',
          name: 'Acme Music',
          subdomain: 'acme-music',
          status: 'healthy',
          uptime: 99.98,
          responseTime: 145,
          errorRate: 0.02,
          resourceUsage: {
            cpu: 35,
            memory: 62,
            storage: 78,
            connections: 120
          },
          lastChecked: new Date().toISOString(),
          issues: []
        },
        {
          id: 'tenant-2',
          name: 'Digital Arts Co',
          subdomain: 'digital-arts',
          status: 'warning',
          uptime: 99.85,
          responseTime: 320,
          errorRate: 0.15,
          resourceUsage: {
            cpu: 78,
            memory: 85,
            storage: 45,
            connections: 89
          },
          lastChecked: new Date().toISOString(),
          issues: ['High CPU usage', 'Slow response times']
        },
        {
          id: 'tenant-3',
          name: 'Global Media',
          subdomain: 'global-media',
          status: 'critical',
          uptime: 97.2,
          responseTime: 1250,
          errorRate: 2.5,
          resourceUsage: {
            cpu: 95,
            memory: 98,
            storage: 92,
            connections: 245
          },
          lastChecked: new Date().toISOString(),
          issues: ['Service overloaded', 'Database connection issues', 'High error rate']
        }
      ];
    },
    refetchInterval: 30000 // Refetch every 30 seconds
  });

  const getStatusCounts = () => {
    if (!healthData) return { healthy: 0, warning: 0, critical: 0, offline: 0 };
    
    return healthData.reduce((counts: { healthy: number; warning: number; critical: number; offline: number }, tenant: TenantHealth) => {
      counts[tenant.status]++;
      return counts;
    }, { healthy: 0, warning: 0, critical: 0, offline: 0 });
  };

  const getAverageMetrics = () => {
    if (!healthData || healthData.length === 0) {
      return { uptime: 0, responseTime: 0, errorRate: 0 };
    }

    const totals = healthData.reduce((acc: { uptime: number; responseTime: number; errorRate: number }, tenant: TenantHealth) => ({
      uptime: acc.uptime + tenant.uptime,
      responseTime: acc.responseTime + tenant.responseTime,
      errorRate: acc.errorRate + tenant.errorRate
    }), { uptime: 0, responseTime: 0, errorRate: 0 });

    return {
      uptime: totals.uptime / healthData.length,
      responseTime: totals.responseTime / healthData.length,
      errorRate: totals.errorRate / healthData.length
    };
  };

  const statusCounts = getStatusCounts();
  const averageMetrics = getAverageMetrics();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Tenant Health Dashboard</h2>
          <p className="text-gray-400 mt-1">Monitor the health and performance of all tenant instances</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="bg-black/50 border border-purple-500/20 rounded-lg px-4 py-2 text-white focus:border-purple-500/50"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <CosmicButton
            variant="secondary"
            onClick={() => refetch()}
          >
            <RefreshCw className="w-4 h-4" />
          </CosmicButton>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <CosmicCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Healthy Tenants</p>
              <p className="text-2xl font-bold text-green-500">{statusCounts.healthy}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </CosmicCard>

        <CosmicCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Warnings</p>
              <p className="text-2xl font-bold text-yellow-500">{statusCounts.warning}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-yellow-500" />
          </div>
        </CosmicCard>

        <CosmicCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Critical</p>
              <p className="text-2xl font-bold text-red-500">{statusCounts.critical}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </CosmicCard>

        <CosmicCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Avg Uptime</p>
              <p className="text-2xl font-bold text-white">{averageMetrics.uptime.toFixed(2)}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </CosmicCard>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <CosmicCard className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Average Response Time</h3>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-white">{Math.round(averageMetrics.responseTime)}ms</span>
            <TrendingDown className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-sm text-gray-400 mt-2">Across all tenants</p>
        </CosmicCard>

        <CosmicCard className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Error Rate</h3>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-white">{averageMetrics.errorRate.toFixed(2)}%</span>
            {averageMetrics.errorRate < 1 ? 
              <TrendingDown className="w-5 h-5 text-green-500" /> :
              <TrendingUp className="w-5 h-5 text-red-500" />
            }
          </div>
          <p className="text-sm text-gray-400 mt-2">Last {selectedTimeRange}</p>
        </CosmicCard>

        <CosmicCard className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Total Tenants</h3>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold text-white">{healthData?.length || 0}</span>
            <Users className="w-8 h-8 text-purple-500" />
          </div>
          <p className="text-sm text-gray-400 mt-2">Active instances</p>
        </CosmicCard>
      </div>

      {/* Tenant Health List */}
      <CosmicCard>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Tenant Status</h3>
          
          <div className="space-y-4">
            {healthData?.map((tenant: TenantHealth) => (
              <motion.div
                key={tenant.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gray-800 rounded-lg p-4 hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`${statusColors[tenant.status as keyof typeof statusColors]}`}>
                      {statusIcons[tenant.status as keyof typeof statusIcons]}
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{tenant.name}</h4>
                      <p className="text-sm text-gray-400">{tenant.subdomain}.cosmkdrm.com</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">Uptime</p>
                    <p className="font-medium text-white">{tenant.uptime}%</p>
                  </div>
                </div>

                {/* Resource Usage */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400">CPU</p>
                      <p className="text-sm text-white">{tenant.resourceUsage.cpu}%</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400">Memory</p>
                      <p className="text-sm text-white">{tenant.resourceUsage.memory}%</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400">Storage</p>
                      <p className="text-sm text-white">{tenant.resourceUsage.storage}%</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400">Response</p>
                      <p className="text-sm text-white">{tenant.responseTime}ms</p>
                    </div>
                  </div>
                </div>

                {/* Issues */}
                {tenant.issues && tenant.issues.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-red-400 mb-2">Issues:</p>
                    <div className="flex flex-wrap gap-2">
                      {tenant.issues.map((issue: string, index: number) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400"
                        >
                          {issue}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center mt-3">
                  <p className="text-xs text-gray-500">
                    Last checked: {new Date(tenant.lastChecked).toLocaleTimeString()}
                  </p>
                  <CosmicButton
                    variant="secondary"
                    size="sm"
                    onClick={() => setSelectedTenant(tenant.id)}
                  >
                    View Details
                  </CosmicButton>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </CosmicCard>
    </div>
  );
};